import { HTTP_STATUSES } from "@miplatform/constants";
import {
  API_VERSION,
  ENABLE_SERVER_SIDE_CACHE_HEADER,
  TASK_SERVICE,
  X_API_VERSION,
} from "../../../constants";
import { apiOptions, apiRequest } from "../../../utils";

const API_ATTEMPT = `${TASK_SERVICE}/attempts`;

export const attempts = {
  /**
   * Retrieves all attempts with optional filters, sorting, and pagination.
   * @async
   * @param {Object} [options={}] - Options for the request.
   * @param {Object} [options.filters] - Filters to apply.
   * @param {Object} [options.sort] - Sorting options.
   * @param {Object} [options.params] - Additional query parameters.
   * @param {AbortSignal} [options.signal] - Abort signal for cancellation.
   * @param {Object} [options.toastMessageConfig] - Toast message configuration.
   * @param {number} [options.top] - Number of items to retrieve.
   * @param {number} [options.skip] - Number of items to skip.
   * @param {boolean} [options.useCache=false] - Whether to use server-side cache.
   */
  getAll: async ({
    filters,
    sort,
    params,
    signal,
    toastMessageConfig,
    top,
    skip,
    useCache = false,
  } = {}) => {
    const options = await apiOptions({
      url: API_ATTEMPT,
      method: "get",
      headers: {
        [X_API_VERSION]: API_VERSION,
        ...(useCache && { [ENABLE_SERVER_SIDE_CACHE_HEADER]: true }),
      },
      signal,
      params,
      filters,
      customFilterString: typeof filters === "string",
      sort,
      top,
      skip,
    });
    return apiRequest(options, toastMessageConfig);
  },

  /**
   * Retrieves all work orders with optional filters, sorting, and pagination.
   * @async
   * @param {Object} [options={}] - Options for the request.
   * @param {Object} [options.filters] - Filters to apply.
   * @param {Object} [options.sort] - Sorting options.
   * @param {Object} [options.params] - Additional query parameters.
   * @param {AbortSignal} [options.signal] - Abort signal for cancellation.
   * @param {Object} [options.toastMessageConfig] - Toast message configuration.
   * @param {number} [options.top] - Number of items to retrieve.
   * @param {number} [options.skip] - Number of items to skip.
   * @param {boolean} [options.useCache=false] - Whether to use server-side cache.
   */
  getAllWorkOrders: async ({
    filters,
    sort,
    params,
    signal,
    toastMessageConfig,
    top,
    skip,
    useCache = false,
  } = {}) => {
    const options = await apiOptions({
      url: `${API_ATTEMPT}-work-orders`,
      method: "get",
      headers: {
        [X_API_VERSION]: API_VERSION,
        ...(useCache && { [ENABLE_SERVER_SIDE_CACHE_HEADER]: true }),
      },
      signal,
      params,
      filters,
      customFilterString: typeof filters === "string",
      sort,
      top,
      skip,
    });
    return apiRequest(options, toastMessageConfig);
  },
  get: async (id) => {
    const options = await apiOptions({
      url: `${API_ATTEMPT}/${id}`,
      method: "get",
      headers: { [X_API_VERSION]: API_VERSION },
    });
    return apiRequest(options);
  },
  update: async ({ id, data }) => {
    const options = await apiOptions({
      url: `${API_ATTEMPT}/${id}`,
      method: "patch",
      headers: { [X_API_VERSION]: API_VERSION },
      data,
    });
    const response = await apiRequest(options);
    if (response.status === HTTP_STATUSES.OK) {
      return response.data;
    }
    return null;
  },
  hold: async ({ id, data, modifiedOn, toastMessageConfig }) => {
    const options = await apiOptions({
      url: `${API_ATTEMPT}/${id}/hold`,
      method: "post",
      headers: { [X_API_VERSION]: API_VERSION, "if-match": modifiedOn },
      data,
    });

    return apiRequest(options, toastMessageConfig);
  },
  holdActivities: async ({ id, data, toastMessageConfig }) => {
    const options = await apiOptions({
      url: `${API_ATTEMPT}/${id}/activities`,
      method: "post",
      headers: { [X_API_VERSION]: API_VERSION },
      data,
    });
    const response = await apiRequest(options, toastMessageConfig);
    if (response.status === HTTP_STATUSES.OK) {
      return response.data;
    }
    return null;
  },
  getStartJobOptions: (id, data) =>
    apiOptions({
      url: `${API_ATTEMPT}/${id}/start`,
      method: "post",
      headers: { [X_API_VERSION]: API_VERSION },
      data,
    }),
  startJob: async (id, data) => {
    const options = await attempts.getStartJobOptions(id, data);
    const response = await apiRequest(options);
    if (response.status === HTTP_STATUSES.OK) {
      return response.data;
    }
    return null;
  },
  getStartRouteOptions: (id, data) =>
    apiOptions({
      url: `${API_ATTEMPT}/${id}/enroute`,
      method: "post",
      headers: { [X_API_VERSION]: API_VERSION },
      data,
    }),
  startRoute: async (id, data) => {
    const options = await attempts.getStartRouteOptions(id, data);
    const response = await apiRequest(options);
    if (response.status === HTTP_STATUSES.OK) {
      return response.data;
    }
    return null;
  },
  lock: async ({ attemptId, data }) => {
    const options = await apiOptions({
      url: `${API_ATTEMPT}/${attemptId}/lock`,
      method: "post",
      headers: { [X_API_VERSION]: API_VERSION },
      data,
    });
    const response = await apiRequest(options);
    if (response.status === HTTP_STATUSES.OK) {
      return response.data;
    }
    return null;
  },
  getUnlockOptions: ({ attemptId, modifiedOn }) =>
    apiOptions({
      url: `${API_ATTEMPT}/${attemptId}/lock`,
      method: "delete",
      headers: { [X_API_VERSION]: API_VERSION },
      modifiedOn,
    }),
  dispatch: async ({ data, modifiedOn, toastMessageConfig }) => {
    const options = await apiOptions({
      url: `${API_ATTEMPT}/dispatch`,
      method: "post",
      headers: { [X_API_VERSION]: API_VERSION, "if-match": modifiedOn },
      data,
    });

    return apiRequest(options, toastMessageConfig);
  },
  bulkAssign: async ({ data, toastMessageConfig }) => {
    const options = await apiOptions({
      url: `${API_ATTEMPT}/bulk-assign`,
      method: "post",
      headers: { [X_API_VERSION]: API_VERSION },
      data,
    });

    return apiRequest(options, toastMessageConfig);
  },
  start: async ({ data, modifiedOn, toastMessageConfig }) => {
    const options = await apiOptions({
      url: `${API_ATTEMPT}/start`,
      method: "post",
      headers: { [X_API_VERSION]: API_VERSION, "if-match": modifiedOn },
      data,
    });
    const response = await apiRequest(options, toastMessageConfig);
    if (response.status === HTTP_STATUSES.OK) {
      return response.data;
    }
    return null;
  },
  submit: async ({ data, modifiedOn, toastMessageConfig }) => {
    const options = await apiOptions({
      url: `${API_ATTEMPT}/submit`,
      method: "post",
      headers: { [X_API_VERSION]: API_VERSION, "if-match": modifiedOn },
      data,
    });
    const response = await apiRequest(options, toastMessageConfig);
    if (response.status === HTTP_STATUSES.OK) {
      return response.data;
    }
    return null;
  },
  stop: async ({ data, modifiedOn }) => {
    const options = await apiOptions({
      url: `${API_ATTEMPT}/stop`,
      method: "post",
      headers: { [X_API_VERSION]: API_VERSION, "if-match": modifiedOn },
      data,
    });
    const response = await apiRequest(options);
    if (response.status === HTTP_STATUSES.OK) {
      return response.data;
    }
    return null;
  },
  /**
   * Starts closeout for an attempt.
   *
   * @param {Object} data - The data to submit with the closeout request.
   * @param {string} data.attemptId - The attempt ID to be closed out.
   * @returns {Promise<Object>} The response from the API request.
   */
  closeoutInProgress: async ({ data, modifiedOn, toastMessageConfig }) => {
    const options = await apiOptions({
      url: `${API_ATTEMPT}/closeoutinprogress`,
      method: "post",
      headers: { [X_API_VERSION]: API_VERSION, "if-match": modifiedOn },
      data,
    });

    return apiRequest(options, toastMessageConfig);
  },
  getCloseoutInProgressOptions: ({ data, modifiedOn }) =>
    apiOptions({
      url: `${API_ATTEMPT}/closeoutinprogress`,
      method: "post",
      headers: { [X_API_VERSION]: API_VERSION, "if-match": modifiedOn },
      data,
    }),
  /**
   * Closes out an attempt.
   *
   * @param {Object} data - The data to submit with the closeout request.
   * @param {string} data.attemptId - The attempt ID to be closed out.
   * @returns {Promise<Object>} The response from the API request.
   */
  closeout: async ({ data, modifiedOn, toastMessageConfig }) => {
    const options = await apiOptions({
      url: `${API_ATTEMPT}/closeout`,
      method: "post",
      headers: { [X_API_VERSION]: API_VERSION, "if-match": modifiedOn },
      data,
    });
    return apiRequest(options, toastMessageConfig);
  },
  /**
   * Create an issue for a task.
   *
   * @param {string} attemptId - The ID of the attempt that has the task to create an issue.
   * @param {Object} data - The data of the issue to create.
   * @param {string} data.description - Issue description.
   * @param {Array} data.images - An array of images to attach to the issue, each image should have a documentId and an imageType of "ORIGINATING".
   * @returns {Promise<Object>} The response from the API request.
   */
  createIssue: async ({ attemptId, data, toastMessageConfig }) => {
    const options = await apiOptions({
      url: `${API_ATTEMPT}/${attemptId}/issues`,
      method: "post",
      headers: { [X_API_VERSION]: API_VERSION },
      data,
    });

    return apiRequest(options, toastMessageConfig);
  },
  /**
   * Gets the latest attempt for the given assignment id
   *
   * @param {string} assignmentId - The id of the assignment
   * @returns {Promise<Object>} - The attempt object
   */
  getLatestAttempt: async (assignmentId) => {
    const options = await apiOptions({
      url: `${TASK_SERVICE}/assignments/${assignmentId}/latest-attempt`,
      method: "get",
      headers: { [X_API_VERSION]: API_VERSION },
    });
    return apiRequest(options);
  },
  createTemporaryAssignment: async ({
    assignmentTemplateId,
    homeId,
    toastMessageConfig,
  }) => {
    const options = await apiOptions({
      url: `${TASK_SERVICE}/assignments/temporary-create-assignment/${assignmentTemplateId}?homeId=${homeId}`,
      method: "post",
      headers: { [X_API_VERSION]: API_VERSION },
    });

    return apiRequest(options, toastMessageConfig);
  },
  createAttemptForMultiTechnician: async ({
    assignmentId,
    data,
    toastMessageConfig,
  }) => {
    const options = await apiOptions({
      url: `${TASK_SERVICE}/assignments/${assignmentId}/attempts/multi-technician`,
      method: "post",
      headers: { [X_API_VERSION]: API_VERSION },
      data,
    });
    return apiRequest(options, toastMessageConfig);
  },
  getScheduleOptions: async ({
    attemptId,
    data,
    lock = false,
    orderModifiedOn,
  }) =>
    apiOptions({
      url: `${API_ATTEMPT}/${attemptId}/schedule${
        lock ? "?acceptLock=True" : ""
      }`,
      method: "post",
      headers: { [X_API_VERSION]: API_VERSION, "if-match": orderModifiedOn },
      data,
    }),
  getRescheduleOptions: async ({ attemptId, data, lock = false }) =>
    apiOptions({
      url: `${API_ATTEMPT}/${attemptId}/reschedule${
        lock ? "?acceptLock=True" : ""
      }`,
      method: "patch",
      headers: { [X_API_VERSION]: API_VERSION },
      data,
    }),
  reassign: async ({ attemptId, data, toastMessageConfig, lock = false }) => {
    const options = await apiOptions({
      url: `${API_ATTEMPT}/${attemptId}/reschedule${
        lock ? "?acceptLock=True" : ""
      }`,
      method: "patch",
      headers: { [X_API_VERSION]: API_VERSION },
      data,
    });
    return apiRequest(options, toastMessageConfig);
  },
  unscheduleOptions: async ({ attemptId, modifiedOn }) => {
    return apiOptions({
      url: `${API_ATTEMPT}/${attemptId}/unschedule`,
      method: "patch",
      headers: { [X_API_VERSION]: API_VERSION },
      modifiedOn,
    });
  },
  unschedule: async ({ attemptId, modifiedOn, toastMessageConfig }) => {
    const options = await apiOptions({
      url: `${API_ATTEMPT}/${attemptId}/unschedule`,
      method: "patch",
      headers: { [X_API_VERSION]: API_VERSION },
      modifiedOn,
    });

    return apiRequest(options, toastMessageConfig);
  },

  /**
   * Cancels an attempt.
   *
   * @param {Object} params - The parameters for the request.
   * @param {string} params.attemptId - The ID of the attempt to cancel.
   * @param {Object} params.data - The data to be sent in the request body.
   * @param {string} params.data.reason - The reason for cancellation.
   * @param {Object} [params.toastMessageConfig] - Optional configuration for toast messages.
   * @returns {Promise<Object>} - A promise that resolves to the response from the API request.
   */
  cancel: async ({ attemptId, data, toastMessageConfig }) => {
    const options = await apiOptions({
      url: `${API_ATTEMPT}/${attemptId}/cancel`,
      method: "post",
      headers: { [X_API_VERSION]: API_VERSION },
      data,
    });

    return apiRequest(options, toastMessageConfig);
  },
  getEmailHistory: async ({ attemptId, toastMessageConfig }) => {
    const options = await apiOptions({
      url: `${API_ATTEMPT}/${attemptId}/emails`,
      method: "get",
      headers: { [X_API_VERSION]: API_VERSION },
    });

    return apiRequest(options, toastMessageConfig);
  },
  getEmailTemplateContacts: async ({
    attemptId,
    emailType,
    toastMessageConfig,
  }) => {
    const options = await apiOptions({
      url: `${API_ATTEMPT}/${attemptId}/emailtypes/${emailType}/contacts`,
      method: "get",
      headers: { [X_API_VERSION]: API_VERSION },
    });

    return apiRequest(options, toastMessageConfig);
  },
  sendEmail: async ({ attemptId, data, toastMessageConfig }) => {
    const options = await apiOptions({
      url: `${API_ATTEMPT}/${attemptId}/send-email`,
      method: "post",
      headers: { [X_API_VERSION]: API_VERSION },
      data,
    });

    return apiRequest(options, toastMessageConfig);
  },
  clearScheduleOptions: async ({ data }) => {
    return apiOptions({
      url: `${API_ATTEMPT}/clear-schedule`,
      method: "post",
      headers: { [X_API_VERSION]: API_VERSION },
      data,
    });
  },
  /**
   * Clears the schedule for a given resource.
   *
   * @async
   * @param {Object} params - Parameters for the request.
   * @param {Object} params.data - The data for clearing the schedule.
   * @param {string} params.data.resourceId - The GUID of the resource whose schedule is to be cleared.
   * @param {string} params.data.date - The date and time (ISO 8601 DateTimeOffset) for the schedule to be cleared.
   * @param {string} [params.data.reason] - Optional reason for clearing the schedule.
   * @param {Object} [params.toastMessageConfig] - Optional toast message configuration.
   * @returns {Promise<Object>} The response from the API request.
   */
  clearSchedule: async ({ data, toastMessageConfig }) => {
    const options = await apiOptions({
      url: `${API_ATTEMPT}/clear-schedule`,
      method: "post",
      headers: { [X_API_VERSION]: API_VERSION },
      data,
    });

    return apiRequest(options, toastMessageConfig);
  },
  handleOpen: async (id, data) => {
    const options = await apiOptions({
      url: `${API_ATTEMPT}/${id}/handle-open?returnAttempt=${
        data?.returnAttempt || false
      }`,
      method: "put",
      headers: { [X_API_VERSION]: API_VERSION, "if-match": data?.modifiedOn },
    });
    return apiRequest(options);
  },
  handleClose: async (id, data) => {
    const options = await apiOptions({
      url: `${API_ATTEMPT}/${id}/handle-close?returnAttempt=${
        data?.returnAttempt || false
      }`,
      method: "put",
      headers: { [X_API_VERSION]: API_VERSION, "if-match": data?.modifiedOn },
    });
    return apiRequest(options);
  },
  handlePending: async (id, data) => {
    const options = await apiOptions({
      url: `${API_ATTEMPT}/${id}/handle-pending?returnAttempt=${
        data?.returnAttempt || false
      }`,
      method: "put",
      data,
      headers: { [X_API_VERSION]: API_VERSION, "if-match": data?.modifiedOn },
    });
    return apiRequest(options);
  },
};
